/**
 * HTTP client for human-agent-service workspace settings (`/api/v1/settings/*`).
 *
 * Requests go through Kong with the same headers as other tenant dashboard calls (see `tenantApiFetch`):
 * - **Authentication:** `Authorization: Bearer <access_token>` (or `X-Auth-Token` if the app uses it).
 * - **Tenant:** `X-Tenant-ID` / `X-Realm-Name` are set by the gateway after token validation (not trusted from raw browser input alone).
 * - **Workspace:** `X-Workspace-Id` from `setTenantApiWorkspaceId` (active workspace from `/me` / workspace switcher).
 * - **Authorization:** Kong forwards `X-Resource-Access-B64` (workspace client roles). Human-agent enforces admin for PUT and **JWT-derived dashboard page scopes** when that header is present (no DB matrix).
 *
 * @see docs/SECURITY.md — never set workspace id from untrusted input; use WorkspaceContext.
 */

import { tenantApiFetch } from "@/lib/tenantApi";

const PREFIX = "/api/v1/settings";

/**
 * Thin gateway to human-agent settings endpoints (SLA, routing, notifications, org defaults).
 */
export class WorkspaceHumanAgentSettingsClient {
  /** @returns {Promise<Record<string, unknown>>} */
  getSla() {
    return tenantApiFetch(`${PREFIX}/sla`);
  }

  /** @param {Record<string, unknown>} body snake_case fields */
  putSla(body) {
    return tenantApiFetch(`${PREFIX}/sla`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  }

  /** @returns {Promise<Record<string, unknown>>} */
  getRouting() {
    return tenantApiFetch(`${PREFIX}/routing`);
  }

  /** @param {Record<string, unknown>} body */
  putRouting(body) {
    return tenantApiFetch(`${PREFIX}/routing`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  }

  /** @returns {Promise<Record<string, unknown>>} */
  getCentralRouting() {
    return tenantApiFetch(`${PREFIX}/central-routing`);
  }

  /** @param {Record<string, unknown>} body */
  putCentralRouting(body) {
    return tenantApiFetch(`${PREFIX}/central-routing`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  }

  /** @returns {Promise<Record<string, unknown>>} */
  getNotifications() {
    return tenantApiFetch(`${PREFIX}/notifications`);
  }

  /** @param {Record<string, unknown>} body */
  putNotifications(body) {
    return tenantApiFetch(`${PREFIX}/notifications`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  }

  /** @returns {Promise<Record<string, unknown>>} */
  getOrgDefaults() {
    return tenantApiFetch(`${PREFIX}/org-defaults`);
  }

  /** @param {Record<string, unknown>} body */
  putOrgDefaults(body) {
    return tenantApiFetch(`${PREFIX}/org-defaults`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  }
}

export const workspaceHumanAgentSettingsClient = new WorkspaceHumanAgentSettingsClient();
